import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Routes, Route } from 'react-router-dom';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import BackfillPage from './pages/BackfillPage';
import { getCurrentUser, checkAuthStatus, authenticateWithPropel, getAvailablePropelOrgs, selectPropelOrg } from './services/apiService';
import './App.css';

function App() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isOnshapeAuthenticated, setIsOnshapeAuthenticated] = useState(false);
  const [isPropelAuth, setIsPropelAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authInProgress, setAuthInProgress] = useState(false);
  const [authLoopDetected, setAuthLoopDetected] = useState(false);
  const [propelOrgs, setPropelOrgs] = useState([]);
  const [selectedPropelOrg, setSelectedPropelOrg] = useState(null);

  useEffect(() => {
    async function checkAuth() {
      const authData = await checkAuthStatus();
      if (authData.authLoop) {
        setAuthLoopDetected(true);
        setIsLoading(false);
        return;
      }
      setIsOnshapeAuthenticated(authData.isOnshapeAuthenticated);
      setIsPropelAuth(authData.isPropelAuthenticated);
      setUser(authData.user);
      
      // Load available Propel orgs if Onshape is authenticated
      if (authData.isOnshapeAuthenticated && !authData.isPropelAuthenticated) {
        const orgs = await getAvailablePropelOrgs();
        setPropelOrgs(orgs);
        
        // Auto-select if only one org or if there's a default
        if (orgs.length === 1) {
          setSelectedPropelOrg(orgs[0]);
        } else if (orgs.length > 1) {
          const defaultOrg = orgs.find(org => org.isDefault);
          if (defaultOrg) {
            setSelectedPropelOrg(defaultOrg);
          }
        }
      }
      
      setIsLoading(false);
    }
    checkAuth();
  }, []);

  // This function has been replaced by handlePropelAuthEnhanced

  console.log(`[App.js Rendering] isLoading: ${isLoading}, isOnshapeAuthenticated: ${isOnshapeAuthenticated}, isPropelAuth: ${isPropelAuth}`);

  if (isLoading) {
    return <div className="loading-container">Loading application...</div>;
  }

  // If execution reaches here, isLoading is false.
  // checkAuthStatus handles redirection if Onshape is not authenticated.
  // So, if !isOnshapeAuthenticated, a redirect should have already occurred or is imminent.
  // We primarily decide based on Propel's authentication status, assuming Onshape is (or will be) handled.

  // Handle Propel org selection change
  const handlePropelOrgChange = async (e) => {
    const orgId = parseInt(e.target.value);
    const org = propelOrgs.find(o => o.id === orgId);
    setSelectedPropelOrg(org);
    
    // Save selection to backend
    if (org) {
      await selectPropelOrg(org.id);
    }
  };

  // Enhanced Propel authentication handler
  const handlePropelAuthEnhanced = async () => {
    if (!selectedPropelOrg) {
      alert('Please select a Propel organization first.');
      return;
    }
    
    try {
      setAuthInProgress(true);
      console.log('[App.js] Starting Propel authentication with popup for org:', selectedPropelOrg.orgLabel);
      
      // Use the popup-based authentication flow with selected org
      const success = await authenticateWithPropel(selectedPropelOrg.id);
      
      if (success) {
        console.log('[App.js] Propel authentication successful!');
        setIsPropelAuth(true);
      } else {
        console.error('[App.js] Propel authentication failed or was cancelled');
        alert('Authentication with Propel failed. Please try again or contact support.');
      }
    } catch (error) {
      console.error('[App.js] Error during Propel authentication:', error);
      alert('An error occurred during authentication. Please try again.');
    } finally {
      setAuthInProgress(false);
    }
  };

  if (isOnshapeAuthenticated && !isPropelAuth) {
    // Allow access to /admin without Propel authentication
    const isAdminRoute = location.pathname === '/admin';
    const isBackfillRoute = location.pathname === '/backfill';

    if (isAdminRoute || isBackfillRoute) {
      return (
        <Routes>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/backfill" element={<BackfillPage />} />
        </Routes>
      );
    }

    // For all other routes, require Propel authentication
    return (
      <div className="app-container">
        <div className="propel-auth-prompt">
          <div className="auth-card">
            <h2>Propel Authentication Required</h2>
            <p>This application requires access to your Propel account.</p>

            {propelOrgs.length === 0 ? (
              <div className="error-message">
                <p><strong>No Propel organizations configured</strong></p>
                <p>Please contact your administrator to configure Propel access.</p>
              </div>
            ) : (
              <>
                {propelOrgs.length > 1 && (
                  <div className="propel-org-selector">
                    <label htmlFor="propel-org-select">Select Propel Organization:</label>
                    <select
                      id="propel-org-select"
                      value={selectedPropelOrg?.id || ''}
                      onChange={handlePropelOrgChange}
                      disabled={authInProgress}
                    >
                      <option value="">-- Select Propel Org --</option>
                      {propelOrgs.map(org => (
                        <option key={org.id} value={org.id}>
                          {org.orgLabel} {org.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {propelOrgs.length === 1 && (
                  <p className="single-org-info">
                    Connecting to: <strong>{propelOrgs[0].orgLabel}</strong>
                  </p>
                )}

                {authInProgress ? (
                  <div className="auth-progress">
                    <p><strong>Authentication in progress...</strong></p>
                    <p>A popup window has been opened for Propel authentication.</p>
                    <p>Please complete the authentication in the popup window.</p>
                    <p className="auth-note">If you don't see a popup, please check if it was blocked by your browser.</p>
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <button
                    onClick={handlePropelAuthEnhanced}
                    className="propel-auth-button"
                    disabled={authInProgress || !selectedPropelOrg}
                  >
                    {selectedPropelOrg
                      ? `Connect to ${selectedPropelOrg.orgLabel}`
                      : 'Select Propel Org to Continue'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isOnshapeAuthenticated && isPropelAuth) {
    return (
      // <Router> component removed, context now comes from BrowserRouter in index.js
      <Routes>
        <Route path="/" element={<HomePage user={user} isPropelAuthenticated={isPropelAuth} />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/backfill" element={<BackfillPage />} />
        {/* Add other routes here if needed */}
      </Routes>
      // </Router>
    );
  }
  
  // Auth redirect loop detected — session is not persisting (likely a browser cookie/SameSite issue).
  // Show an actionable error instead of looping indefinitely.
  if (authLoopDetected) {
    return (
      <div className="loading-container" style={{ textAlign: 'center', padding: '40px 24px' }}>
        <h3 style={{ marginBottom: '12px' }}>Unable to sign in</h3>
        <p style={{ marginBottom: '16px' }}>
          Your Onshape session could not be established. Please try the following steps:
        </p>
        <ol style={{ textAlign: 'left', display: 'inline-block', marginBottom: '24px', lineHeight: '1.8' }}>
          <li>Log out of Onshape from your browser.</li>
          <li>Log back in to Onshape.</li>
          <li>Reopen this panel from within Onshape.</li>
        </ol>
        <p style={{ marginBottom: '24px', fontSize: '13px', color: '#666' }}>
          If the issue persists, check that your browser allows third-party cookies for this site,
          or try a different browser.
        </p>
        <button
          onClick={() => {
            sessionStorage.removeItem('onshape_auth_redirect_at');
            window.location.href = '/oauthSignin';
          }}
          style={{ padding: '10px 20px', cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }

  // Fallback: brief transitional state while a redirect is in progress.
  return <div className="loading-container">Finalizing authentication...</div>;
}

export default App;
